import { useActions, usePatient, useSettings } from '@/store/selectors';
import { Indication, Sex, FluidStatus, type PatientInput, type ContextModifier } from '@/types';
import { kgToLb, lbToKg, cmToIn, inToCm, mgDlToUmolL, umolLToMgDl } from '@/utils/units';
import { indicationLabel, fluidStatusLabel } from '@/utils/format';
import { Collapsible } from '@/components/shared/Collapsible';
import { ExpressionInput, Select, UnitToggle } from 'calc-controls';

const CONTEXT_TYPES: ContextModifier['type'][] = ['critical-illness', 'obesity', 'augmented-clearance', 'altered-vd'];
const CONTEXT_LABELS: Record<ContextModifier['type'], string> = {
  'critical-illness': 'Critical illness (ICU)',
  'obesity': 'Obesity (BMI > 30)',
  'augmented-clearance': 'Augmented renal clearance (ARC)',
  'altered-vd': 'Altered volume of distribution',
};
const CONTEXT_HINTS: Record<ContextModifier['type'], string> = {
  'critical-illness': 'Vd may be increased; consider early peak/trough confirmation.',
  'obesity': 'Adjusted Vd applied; dosing weight uses AdjBW.',
  'augmented-clearance': 'Subtherapeutic peaks possible; consider q12h pathway.',
  'altered-vd': 'Fluid shifts — early level confirmation recommended.',
};
const NEPHROTOXINS = ['Vancomycin', 'NSAIDs', 'IV contrast', 'Loop diuretics', 'Amphotericin B', 'Cisplatin', 'Other'];

/** Patient demographics + labs form with inline validation (PSD §8.1.2 / TODO Task 1.20). */
export function PatientForm(): JSX.Element {
  const { patient, validationErrors } = usePatient();
  const actions = useActions();
  const settings = useSettings();
  const si = settings.unitSystem === 'si';

  if (!patient) {
    return <div className="empty-state">Enter patient details to begin dosing.</div>;
  }

  const setField = (field: keyof PatientInput, value: unknown): void => {
    actions.setPatientField(field, value);
  };

  // Engine stores kg/cm/mg-dL; SI = metric (kg/cm/µmol-L), Conventional = imperial (lb/in/mg-dL).
  // SCr: SI label flips to µmol/L and value is converted; mg/dL always shown for Conventional.
  const weightDisplay = si ? patient.actualWeightKg : kgToLb(patient.actualWeightKg);
  const heightDisplay = si ? patient.heightCm : cmToIn(patient.heightCm);
  const scrDisplay = si ? mgDlToUmolL(patient.scrMgDl) : patient.scrMgDl;

  return (
    <section className="panel" aria-label="Patient and labs" data-testid="patient-form">
      <h2 className="panel-title">Patient &amp; Labs</h2>
      <fieldset className="fieldset-plain">
        <legend className="fieldset-legend">Demographics</legend>
        <div className="field-row">
        <div className={`field${validationErrors.ageYears ? ' has-error' : ''}`} data-testid="field-ageYears">
          <ExpressionInput
            id="age"
            label="Age"
            unit="yr"
            value={patient.ageYears}
            onChange={(v) => setField('ageYears', v)}
            min={0}
            max={120}
            decimals={0}
            inline
            aria-invalid={!!validationErrors.ageYears}
            aria-describedby={validationErrors.ageYears ? 'error-age' : undefined}
          />
          {validationErrors.ageYears && <span id="error-age" className="field-error" role="alert">{validationErrors.ageYears}</span>}
        </div>
        <div data-testid="field-sex">
          <UnitToggle
            label="Sex"
            options={[
              { label: 'Male', value: Sex.Male },
              { label: 'Female', value: Sex.Female },
            ]}
            value={patient.sex}
            onChange={(v) => setField('sex', v as Sex)}
          />
        </div>
      </div>
      <div className="field-row">
        <div className={`field${validationErrors.heightCm ? ' has-error' : ''}`} data-testid="field-heightCm">
          <ExpressionInput
            id="height"
            label="Height"
            unit={si ? 'cm' : 'in'}
            value={Number(heightDisplay.toFixed(1))}
            onChange={(v) => setField('heightCm', si ? v : inToCm(v))}
            min={0}
            max={300}
            decimals={1}
            inline
            aria-invalid={!!validationErrors.heightCm}
            aria-describedby={validationErrors.heightCm ? 'error-height' : undefined}
          />
          {validationErrors.heightCm && <span id="error-height" className="field-error" role="alert">{validationErrors.heightCm}</span>}
        </div>
        <div className={`field${validationErrors.actualWeightKg ? ' has-error' : ''}`} data-testid="field-actualWeightKg">
          <ExpressionInput
            id="weight"
            label="Weight"
            unit={si ? 'kg' : 'lb'}
            value={Number(weightDisplay.toFixed(1))}
            onChange={(v) => setField('actualWeightKg', si ? v : lbToKg(v))}
            min={0}
            max={500}
            decimals={1}
            inline
            aria-invalid={!!validationErrors.actualWeightKg}
            aria-describedby={validationErrors.actualWeightKg ? 'error-weight' : undefined}
          />
          {validationErrors.actualWeightKg && (
            <span id="error-weight" className="field-error" role="alert">{validationErrors.actualWeightKg}</span>
          )}
        </div>
      </div>
      </fieldset>

      <fieldset className="fieldset-plain">
        <legend className="fieldset-legend">Labs &amp; Indication</legend>
        <div className="field-row">
          <div className={`field${validationErrors.scrMgDl ? ' has-error' : ''}`} data-testid="field-scrMgDl" title="Serum creatinine. Used for Cockcroft-Gault CrCl and population kₑl estimation.">
          <ExpressionInput
            id="scr"
            label="SCr"
            unit={si ? 'µmol/L' : 'mg/dL'}
            value={si ? Math.round(scrDisplay) : Number(scrDisplay.toFixed(2))}
            onChange={(v) => setField('scrMgDl', si ? umolLToMgDl(v) : v)}
            min={0}
            decimals={si ? 0 : 2}
            inline
            aria-invalid={!!validationErrors.scrMgDl}
            aria-describedby={validationErrors.scrMgDl ? 'error-scr' : undefined}
          />
          {validationErrors.scrMgDl && <span id="error-scr" className="field-error" role="alert">{validationErrors.scrMgDl}</span>}
        </div>
        <div className="field">
          <label htmlFor="scr-time">SCr drawn</label>
          <input
            id="scr-time"
            type="datetime-local"
            data-testid="field-scrTimestamp"
            onChange={(e) => setField('scrTimestamp', e.target.value)}
          />
        </div>
      </div>
      <div className="field-row">
        <div data-testid="field-indication">
          <Select
            label="Indication"
            tooltip="Sets agent-specific target peak/trough ranges and dose intensity multiplier."
            options={Object.values(Indication).map((ind) => ({ value: ind, label: indicationLabel(ind) }))}
            value={patient.indication}
            onChange={(v) => actions.setIndication(v as Indication)}
          />
        </div>
        <div>
          <Select
            label="Fluid status"
            tooltip="Affects volume of distribution assessment. Hypervolemia may increase Vd; hypovolemia may decrease it."
            options={Object.values(FluidStatus).map((f) => ({ value: f, label: fluidStatusLabel(f) }))}
            value={patient.fluidStatus}
            onChange={(v) => setField('fluidStatus', v as FluidStatus)}
          />
        </div>
      </div>

      <div className="field" title="Blood urea nitrogen. Used alongside SCr for renal monitoring frequency assessment.">
        <ExpressionInput
          id="bun"
          label="BUN (optional)"
          unit="mg/dL"
          value={patient.bunMgDl ?? 0}
          onChange={(v) => setField('bunMgDl', v || undefined)}
          min={0}
          decimals={0}
          inline
        />
      </div>
      </fieldset>

      <Collapsible title="Clinical context modifiers" defaultOpen={false}>
        <fieldset className="fieldset-plain">
          <legend className="hint-text">Patient-specific factors affecting PK/toxicity</legend>
          {CONTEXT_TYPES.map((t) => {
            const checked = patient.contextModifiers?.some((m) => m.type === t && m.active) ?? false;
            return (
              <div key={t} className="field">
                <label htmlFor={`ctx-${t}`} className="checkbox-label">
                  <input
                    id={`ctx-${t}`}
                    type="checkbox"
                    checked={checked}
                    title={CONTEXT_HINTS[t]}
                    onChange={(e) => actions.setContextModifier(t, e.target.checked)}
                    data-testid={`field-ctx-${t}`}
                  />
                  {CONTEXT_LABELS[t]}
                </label>
              </div>
            );
          })}
        </fieldset>
      </Collapsible>

      <Collapsible title="Concurrent nephrotoxins" defaultOpen={false}>
        <fieldset className="fieldset-plain">
          <legend className="hint-text">Select all concurrent nephrotoxic agents</legend>
          {NEPHROTOXINS.map((name) => {
            const checked = patient.concurrentNephrotoxins?.some((n) => n.name === name && n.active) ?? false;
            return (
              <div key={name} className="field">
                <label htmlFor={`tox-${name}`} className="checkbox-label">
                  <input
                    id={`tox-${name}`}
                    type="checkbox"
                    checked={checked}
                    data-testid={`field-tox-${name}`}
                    onChange={(e) => {
                      const current = patient.concurrentNephrotoxins?.filter((n) => n.name !== name) ?? [];
                      actions.setConcurrentNephrotoxins([...current, { name, active: e.target.checked }]);
                    }}
                  />
                  {name}
                </label>
              </div>
            );
          })}
        </fieldset>
      </Collapsible>
    </section>
  );
}
